// D21-1 — el control de "de dónde salen las opciones", para los DOS diálogos.
//
// Personas (FieldsSection) y Procesos (PipelinesSection) editan el mismo dato
// desde dos pantallas distintas, así que la pregunta se escribe una vez: el
// selector con sus tres orígenes, y la lista VIVA de solo lectura que reemplaza
// al textarea cuando el dato toma sus opciones de una fuente. Dos copias de este
// bloque serían dos redacciones de la misma regla, y una de ellas se quedaría
// atrás el día que se agregue una tercera fuente.
//
// LA LISTA NO SE EDITA AQUÍ, Y SE DICE DÓNDE SÍ. Un dato con fuente no guarda
// opciones (el API lo rechaza con un 409 que nombra la pantalla), así que
// enseñarla en un textarea sería una puerta pintada: se enseña como es, en
// chips, con la frase que dice de quién es la lista y el enlace a la pantalla
// que la cambia.
//
// EL GIRO DECIDE QUÉ FUENTES EXISTEN. Agenda y Catálogo son pantallas de salud
// (la barra lateral las esconde para los demás giros con este mismo hecho:
// `isHealthcareVertical`), así que una casa de empeño no puede apuntar un dato a
// una Agenda que no tiene. El valor que la fila YA trae se ofrece siempre, para
// que un dato con fuente nunca aparezca con un selector que no lo contiene.

import { Link } from 'react-router-dom'

import {
  type FieldOptionsSource,
  OPTIONS_SOURCE_EMPTY,
  OPTIONS_SOURCE_FIELD_LABEL,
  OPTIONS_SOURCE_LABELS,
  OPTIONS_SOURCE_LINK,
  OPTIONS_SOURCE_SENTENCE,
} from './fields-data.js'
import { useSession } from '../../auth/session.js'
import { Chip, FormField, Select } from '../../design-system/index.js'
import { isHealthcareVertical } from '../shell/nav.js'

/** El selector de origen. Se dibuja solo para un dato de tipo lista: es la
 *  única clase de dato que tiene opciones, y el API se niega igual. */
export function OptionsSourceSelect({
  id,
  value,
  onChange,
}: {
  id: string
  value: FieldOptionsSource
  onChange: (next: FieldOptionsSource) => void
}): JSX.Element {
  const session = useSession()
  const hasSalud = isHealthcareVertical(session?.vertical)
  const sources: FieldOptionsSource[] = (['lista', 'agenda', 'catalogo'] as const).filter(
    (s) => s === 'lista' || hasSalud || s === value,
  )
  return (
    <FormField label={OPTIONS_SOURCE_FIELD_LABEL} htmlFor={id}>
      <Select
        id={id}
        value={value}
        options={sources.map((s) => ({ value: s, label: OPTIONS_SOURCE_LABELS[s] }))}
        onChange={(e) => onChange(e.target.value as FieldOptionsSource)}
      />
    </FormField>
  )
}

/**
 * La lista viva, de solo lectura, con la puerta a la pantalla que sí la cambia.
 *
 * `options` llega ya resuelta por el API: es lo que la fuente ofrece HOY. Tres
 * estados, y los tres se dicen distinto porque significan cosas distintas:
 *
 *   - una lista con opciones: se enseñan, y se dice de quién son;
 *   - `[]`: la fuente existe y está VACÍA, que es un estado real (una clínica
 *     sin médicos activos) y hay que decirlo, porque el dato se preguntará como
 *     texto libre y nadie lo descubriría hasta leer una conversación;
 *   - `null`: todavía no hay lista que resolver — el diálogo de CREAR, donde el
 *     dato aún no existe. Anunciar ahí "tu Agenda no tiene médicos" sería
 *     afirmar algo que esta pantalla no sabe.
 */
export function SourcedOptionsList({
  source,
  options,
}: {
  source: Exclude<FieldOptionsSource, 'lista'>
  options: string[] | null
}): JSX.Element {
  const link = OPTIONS_SOURCE_LINK[source]
  return (
    <div className="flex flex-col gap-2" data-testid="sourced-options">
      {options !== null && options.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {options.map((o) => (
            <Chip key={o} tone="info">
              {o}
            </Chip>
          ))}
        </div>
      )}
      {options !== null && options.length === 0 ? (
        <p
          className="text-[12.5px] leading-relaxed text-text-2"
          data-testid="sourced-options-empty"
        >
          {OPTIONS_SOURCE_EMPTY[source]}
        </p>
      ) : (
        <p className="text-[12.5px] leading-relaxed text-text-2">
          {OPTIONS_SOURCE_SENTENCE[source]}
        </p>
      )}
      <Link to={link.to} className="text-[12.5px] font-semibold text-link hover:underline">
        {link.label}
      </Link>
    </div>
  )
}
